// LeaderboardTab — Tab 3: Top-Wallets nach PnL/AV/Volume/ROI/EVE-Score.
// Layout:
//   1. Smart-Filter-Bar (EVE-Filter / Min-AV / Window / Sort)
//   2. DataTable mit Rank-Spalte + Quality-Badge + Pagination
//
// EVE-Zugang: globaler `EvePresenceDrawer`, kein eigener Composer (Phase 4Q).
// Daten: useHlLeaderboard() — Hier nur die Filter-State-Maschinerie.

import { useEffect, useMemo, useState } from 'react';
import { Check, ExternalLink as ExtIcon, Eye, Info, X, Zap } from 'lucide-react';
import {
  DataTable,
  ExternalLinkButton,
  QualityBadge,
  type DataTableColumn,
} from '@/components/ui/data-table';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import type { WalletQuality } from '@/lib/hl-quality';

export type LeaderboardMinAv = 'all' | '1k' | '10k' | '100k' | '1m';
export type LeaderboardWindow = 'day' | 'week' | 'month' | 'all';
export type LeaderboardSort = 'pnl' | 'av' | 'volume' | 'roi' | 'eveScore';

export interface LeaderboardRow {
  id: string;
  rank: number;
  label?: string;
  address: string;
  accountValueUsd: number;
  pnlUsd: number;
  roiPct: number;
  volumeUsd: number;
  quality: WalletQuality;
  /** Optional Initials für den Avatar-Kreis (z.B. „CC" für CryptoChad). */
  avatarInitials?: string;
  /** Avatar-Tone (default amber). */
  avatarTone?: 'amber' | 'emerald' | 'rose' | 'neutral';
  explorerUrl?: string;
}

interface LeaderboardTabProps {
  rows: ReadonlyArray<LeaderboardRow>;
  /** Total-Counts für „X Wallets · Y gefiltert" Header. */
  totalCount?: number;
  loading?: boolean;
  /** Adressen die bereits im Wallet-Tracker liegen — Preview-Modal zeigt
   *  „bereits getrackt" State und disabled-Button. */
  trackedAddresses?: ReadonlyArray<string>;
  /** Action „In Wallet-Tracker hinzufügen" aus dem Preview-Modal. Address
   *  + optional Label (Leaderboard-Display-Name) werden durchgereicht. */
  onAddToTracker?: (input: { address: string; label?: string | null }) => Promise<void> | void;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export function LeaderboardTab({
  rows,
  totalCount,
  loading,
  trackedAddresses,
  onAddToTracker,
}: LeaderboardTabProps) {
  const [eveFilter, setEveFilter] = useState(true);
  const [minAv, setMinAv] = useState<LeaderboardMinAv>('10k');
  const [window, setWindow] = useState<LeaderboardWindow>('all');
  const [sort, setSort] = useState<LeaderboardSort>('pnl');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState<PageSize>(25);
  const [page, setPage] = useState(0);
  const [preview, setPreview] = useState<LeaderboardRow | null>(null);

  // Lower-case Set für O(1) tracked-Check (Adressen aus DB können
  // case-insensitive sein).
  const trackedSet = useMemo(() => {
    const s = new Set<string>();
    for (const a of trackedAddresses ?? []) s.add(a.toLowerCase());
    return s;
  }, [trackedAddresses]);

  function isTracked(addr: string): boolean {
    return trackedSet.has(addr.toLowerCase());
  }

  const filteredRows = useMemo(
    () => sortRows(applyFilters(rows, { eveFilter, minAv, search }), sort),
    [rows, eveFilter, minAv, search, sort],
  );

  // Bei Filter/Search/Sort-Änderung zurück auf Seite 1.
  useEffect(() => {
    setPage(0);
  }, [eveFilter, minAv, search, sort, pageSize, rows.length]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const visibleRows = useMemo(
    () => filteredRows.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [filteredRows, safePage, pageSize],
  );

  const columns: DataTableColumn<LeaderboardRow>[] = [
    { key: 'rank', header: '#', cell: (r) => <span className="text-neutral-400">{r.rank}</span>, thClassName: 'w-10', toggleable: false },
    {
      key: 'wallet',
      header: 'Wallet',
      cell: (r) => (
        <div className="flex items-center gap-2">
          <div
            className={`grid size-6 place-items-center rounded-full text-[10px] font-bold ${avatarBg(r.avatarTone)}`}
          >
            {r.avatarInitials ?? '?'}
          </div>
          <div>
            {r.label ? (
              <div className="text-[12px] font-bold text-amber-400">{r.label}</div>
            ) : null}
            <div className="flex items-center gap-1 font-mono text-[11px] text-neutral-500">
              {shortAddr(r.address)}
              {r.explorerUrl ? <ExternalLinkButton href={r.explorerUrl} label="Wallet" /> : null}
            </div>
          </div>
        </div>
      ),
    },
    { key: 'account', header: 'Account', align: 'right', cell: (r) => <span className="font-bold">{fmtBig(r.accountValueUsd)}</span> },
    {
      key: 'pnl',
      header: 'PnL',
      align: 'right',
      cell: (r) => (
        <span className={r.pnlUsd >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
          {r.pnlUsd >= 0 ? '' : '−'}
          {fmtBig(Math.abs(r.pnlUsd))}
        </span>
      ),
    },
    {
      key: 'roi',
      header: 'ROI',
      align: 'right',
      cell: (r) => (
        <span className={r.roiPct >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
          {r.roiPct >= 0 ? '+' : ''}
          {r.roiPct.toFixed(1)}%
        </span>
      ),
    },
    { key: 'volume', header: 'Volume', align: 'right', cell: (r) => <span className="text-neutral-400">{fmtBig(r.volumeUsd)}</span> },
    {
      key: 'quality',
      header: (
        <span className="inline-flex items-center gap-1">
          Quality
          <span
            className="cursor-help text-neutral-500 hover:text-neutral-300"
            title="Wallet-Klassifizierung nach Account-Value · Volume · PnL:&#10;LEGIT — av≥$100K + (vol≥$10M oder pnl≥$100K) · professionell&#10;OK — Mittelgroß, akzeptables Signal&#10;SMALL — av<$10K · Kleinanleger&#10;DUST — av<$100 · irrelevant&#10;GHOST — av=0 · leer/aufgegeben"
            aria-label="Quality-Klassen erklären"
          >
            <Info className="size-3" />
          </span>
        </span>
      ),
      align: 'right',
      cell: (r) => <QualityBadge quality={r.quality} />,
    },
    {
      key: 'action',
      header: 'Aktion',
      align: 'right',
      cell: (r) => {
        const tracked = isTracked(r.address);
        return (
          <div className="flex items-center justify-end gap-1.5">
            {tracked ? (
              <span
                className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300"
                title="Bereits in deinem Wallet-Tracker"
              >
                ✓ getrackt
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => setPreview(r)}
              className="inline-flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-300 hover:bg-amber-500/25"
              aria-label={`Vorschau für Wallet ${r.address}`}
            >
              <Eye className="size-3" />
              Vorschau
            </button>
          </div>
        );
      },
      toggleable: false,
    },
  ];

  // Anzahl Wallets die durch aktive Filter ausgeblendet werden (Doc-Feedback
  // 2026-05-16: vorher unsichtbar, EVE-Filter-Toggle fühlte sich „ohne Funktion" an).
  const hiddenByFilters = Math.max(0, rows.length - filteredRows.length);

  return (
    <div className="space-y-3">
      <FilterBar
        minAv={minAv}
        onMinAvChange={setMinAv}
        window={window}
        onWindowChange={setWindow}
        sort={sort}
        onSortChange={setSort}
        search={search}
        onSearchChange={setSearch}
      />

      <DataTable
        rows={visibleRows}
        columns={columns}
        rowKey={(r) => r.id}
        title={
          <span className="flex items-center gap-2">
            🐋 Leaderboard
            <span className="text-[11px] font-normal text-neutral-500">
              {filteredRows.length.toLocaleString('en-US')} von{' '}
              {(totalCount ?? rows.length).toLocaleString('en-US')} Wallets
              {hiddenByFilters > 0 ? ` · ${hiddenByFilters.toLocaleString('en-US')} ausgeblendet` : ''}
            </span>
          </span>
        }
        toolbar={
          <>
            <button
              type="button"
              onClick={() => setEveFilter((s) => !s)}
              className={
                eveFilter
                  ? 'flex items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/15 px-2.5 py-1 font-bold text-amber-400'
                  : 'glass flex items-center gap-1 rounded-md px-2.5 py-1 text-neutral-400'
              }
            >
              <Zap className="size-3" />
              EVE Filter {eveFilter ? 'ON' : 'OFF'}
            </button>
          </>
        }
        loading={loading}
        emptyState={
          <div>
            <div>Keine Wallets passend zum Filter.</div>
            <div className="mt-1 text-[11px]">
              Reduziere Min-AV oder schalte EVE-Filter aus.
            </div>
          </div>
        }
        footer={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-neutral-500">Zeilen pro Seite:</span>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPageSize(n)}
                  className={
                    pageSize === n
                      ? 'rounded bg-amber-500/20 px-2 py-0.5 font-bold text-amber-300'
                      : 'rounded px-2 py-0.5 text-neutral-500 hover:text-neutral-300'
                  }
                >
                  {n}
                </button>
              ))}
              <span className="ml-2 text-neutral-500">
                {filteredRows.length > 0
                  ? `${safePage * pageSize + 1}–${Math.min(
                      (safePage + 1) * pageSize,
                      filteredRows.length,
                    )} von ${filteredRows.length.toLocaleString('en-US')}`
                  : '0 Einträge'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(0)}
                disabled={safePage === 0}
                className="rounded px-1.5 py-0.5 hover:text-neutral-300 disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Erste Seite"
              >
                ⏮
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="rounded px-2 py-0.5 hover:text-neutral-300 disabled:cursor-not-allowed disabled:opacity-30"
              >
                ← Prev
              </button>
              <span className="tabular text-neutral-400">
                Seite {safePage + 1} / {pageCount.toLocaleString('en-US')}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={safePage >= pageCount - 1}
                className="rounded px-2 py-0.5 hover:text-neutral-300 disabled:cursor-not-allowed disabled:opacity-30"
              >
                Next →
              </button>
              <button
                type="button"
                onClick={() => setPage(pageCount - 1)}
                disabled={safePage >= pageCount - 1}
                className="rounded px-1.5 py-0.5 hover:text-neutral-300 disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Letzte Seite"
              >
                ⏭
              </button>
            </div>
          </div>
        }
      />

      {preview ? (
        <WalletPreviewModal
          row={preview}
          tracked={isTracked(preview.address)}
          onClose={() => setPreview(null)}
          onAddToTracker={onAddToTracker}
        />
      ) : null}
    </div>
  );
}

function WalletPreviewModal({
  row,
  tracked,
  onClose,
  onAddToTracker,
}: {
  row: LeaderboardRow;
  tracked: boolean;
  onClose: () => void;
  onAddToTracker?: (input: { address: string; label?: string | null }) => Promise<void> | void;
}) {
  const askEve = useEveDrawerStore((s) => s.askEve);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleAdd() {
    if (!onAddToTracker || tracked || adding) return;
    setAdding(true);
    setError(null);
    try {
      await onAddToTracker({ address: row.address, label: row.label ?? null });
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Hinzufügen fehlgeschlagen');
    } finally {
      setAdding(false);
    }
  }

  const pnlTone = row.pnlUsd >= 0 ? 'text-emerald-400' : 'text-rose-400';
  const roiTone = row.roiPct >= 0 ? 'text-emerald-400' : 'text-rose-400';

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="glass-strong w-full max-w-md rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">
              Wallet · Rank #{row.rank}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {row.label ? (
                <h3 className="text-base font-bold text-amber-400">{row.label}</h3>
              ) : null}
              <span className="font-mono text-[11px] text-neutral-500">{shortAddr(row.address)}</span>
              {row.explorerUrl ? (
                <a
                  href={row.explorerUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Wallet im Explorer öffnen"
                  className="text-neutral-500 hover:text-amber-400"
                >
                  <ExtIcon className="size-3" />
                </a>
              ) : null}
              <QualityBadge quality={row.quality} />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="shrink-0 text-neutral-500 hover:text-neutral-200"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="glass rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Account</div>
            <div className="mt-1 text-base font-bold tabular">{fmtBig(row.accountValueUsd)}</div>
          </div>
          <div className="glass rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">All-Time PnL</div>
            <div className={`mt-1 text-base font-bold tabular ${pnlTone}`}>
              {row.pnlUsd >= 0 ? '+' : '−'}
              {fmtBig(Math.abs(row.pnlUsd))}
            </div>
          </div>
          <div className="glass rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">ROI</div>
            <div className={`mt-1 text-base font-bold tabular ${roiTone}`}>
              {row.roiPct >= 0 ? '+' : ''}
              {row.roiPct.toFixed(1)}%
            </div>
          </div>
          <div className="glass rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Volume</div>
            <div className="mt-1 text-base font-bold tabular text-neutral-300">{fmtBig(row.volumeUsd)}</div>
          </div>
        </div>

        {error ? (
          <div className="mb-3 rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {tracked || success ? (
            <button
              type="button"
              disabled
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[12px] font-bold text-emerald-300"
            >
              <Check className="size-3.5" />
              {success ? 'Hinzugefügt' : 'Bereits getrackt'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={!onAddToTracker || adding}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-[12px] font-bold text-neutral-950 hover:bg-amber-400 disabled:bg-neutral-700 disabled:text-neutral-500"
            >
              <Eye className="size-3.5" />
              {adding ? 'Speichert…' : 'In Wallet-Tracker aufnehmen'}
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              askEve(
                `Polymarket-/HL-Wallet ${row.address} (Rank #${row.rank}${row.label ? `, „${row.label}"` : ''}) hat all-time PnL ${row.pnlUsd.toFixed(0)}$ bei Account ${row.accountValueUsd.toFixed(0)}$ (ROI ${row.roiPct.toFixed(1)}%, Vol ${row.volumeUsd.toFixed(0)}$).\nanalysiere und recherchiere die Wallet (Strategie, kürzliche Aktivität) und schau dir die gesamte marktlage an.`,
                {
                  displayText: `Wallet ${row.label ?? shortAddr(row.address)} · PnL ${row.pnlUsd >= 0 ? '+' : '−'}${fmtBig(Math.abs(row.pnlUsd))} · ROI ${row.roiPct.toFixed(1)}%`,
                },
              )
            }
            className="inline-flex items-center gap-1 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] font-bold text-amber-300 hover:bg-amber-500/20"
          >
            <Zap className="size-3.5" />
            Frag EVE
          </button>
        </div>
      </div>
    </div>
  );
}

interface FilterBarProps {
  minAv: LeaderboardMinAv;
  onMinAvChange: (m: LeaderboardMinAv) => void;
  window: LeaderboardWindow;
  onWindowChange: (w: LeaderboardWindow) => void;
  sort: LeaderboardSort;
  onSortChange: (s: LeaderboardSort) => void;
  search: string;
  onSearchChange: (q: string) => void;
}

const MIN_AV_OPTIONS: ReadonlyArray<{ value: LeaderboardMinAv; label: string }> = [
  { value: 'all', label: 'Alle' },
  { value: '1k', label: '$1K' },
  { value: '10k', label: '$10K' },
  { value: '100k', label: '$100K' },
  { value: '1m', label: '$1M' },
];

const WINDOW_OPTIONS: ReadonlyArray<{ value: LeaderboardWindow; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'all', label: 'All' },
];

const SORT_OPTIONS: ReadonlyArray<{ value: LeaderboardSort; label: string }> = [
  { value: 'pnl', label: 'PnL' },
  { value: 'av', label: 'AV' },
  { value: 'volume', label: 'Volume' },
  { value: 'roi', label: 'ROI' },
  { value: 'eveScore', label: '⚡ EVE Score' },
];

function FilterBar({
  minAv,
  onMinAvChange,
  window,
  onWindowChange,
  sort,
  onSortChange,
  search,
  onSearchChange,
}: FilterBarProps) {
  return (
    <div className="glass-strong flex flex-wrap items-center gap-3 rounded-2xl px-4 py-2 text-[12px]">
      <span className="text-neutral-500">Min AV:</span>
      {MIN_AV_OPTIONS.map((o) => (
        <FilterChip key={o.value} active={minAv === o.value} onClick={() => onMinAvChange(o.value)}>
          {o.label}
        </FilterChip>
      ))}
      <div className="h-4 w-px bg-white/10" />
      <span className="text-neutral-500">Window:</span>
      {WINDOW_OPTIONS.map((o) => (
        <FilterChip key={o.value} active={window === o.value} onClick={() => onWindowChange(o.value)}>
          {o.label}
        </FilterChip>
      ))}
      <div className="h-4 w-px bg-white/10" />
      <span className="text-neutral-500">Sort:</span>
      {SORT_OPTIONS.map((o) => (
        <FilterChip key={o.value} active={sort === o.value} onClick={() => onSortChange(o.value)} accent={o.value === 'eveScore'}>
          {o.label}
        </FilterChip>
      ))}
      <div className="flex-1" />
      <div className="glass flex items-center gap-1.5 rounded-md px-2 py-1">
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Wallet/Name…"
          className="w-32 bg-transparent placeholder:text-neutral-600 focus:outline-none"
        />
      </div>
    </div>
  );
}

function FilterChip({
  active,
  accent,
  onClick,
  children,
}: {
  active: boolean;
  accent?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const base = 'rounded px-2 py-0.5 font-bold';
  if (active) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${base} ${accent ? 'border border-amber-500/30 text-amber-300' : 'bg-amber-500/20 text-amber-400'}`}
      >
        {children}
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${base} text-neutral-500 hover:text-neutral-300`}>
      {children}
    </button>
  );
}

function applyFilters(
  rows: ReadonlyArray<LeaderboardRow>,
  opts: { eveFilter: boolean; minAv: LeaderboardMinAv; search: string },
): LeaderboardRow[] {
  const minAvUsd = minAvCutoff(opts.minAv);
  const lower = opts.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (r.accountValueUsd < minAvUsd) return false;
    // EVE-Filter (Doc-Feedback 2026-05-16): zeigt nur Wallets mit Quality
    // LEGIT — also Profis (av≥$100K + vol≥$10M ODER pnl≥$100K). Vorher
    // filterte er nur GHOST+DUST raus, was bei Default minAv=$10K
    // schon durch (1) erledigt war → Filter fühlte sich wirkungslos an.
    if (opts.eveFilter && r.quality !== 'LEGIT') return false;
    if (lower) {
      const hay = `${r.address} ${r.label ?? ''}`.toLowerCase();
      if (!hay.includes(lower)) return false;
    }
    return true;
  });
}

/**
 * Sortiert (in-place mit slice) nach gewähltem Sort-Schlüssel, immer
 * absteigend (Top-Performer oben). EVE-Score = log10(av+1) × log10(vol+1)
 * × (roi+1) — Caller liefert die schon-berechneten Felder, hier nur
 * Kombination.
 */
function sortRows(rows: LeaderboardRow[], sort: LeaderboardSort): LeaderboardRow[] {
  const out = rows.slice();
  if (sort === 'pnl') out.sort((a, b) => b.pnlUsd - a.pnlUsd);
  else if (sort === 'av') out.sort((a, b) => b.accountValueUsd - a.accountValueUsd);
  else if (sort === 'volume') out.sort((a, b) => b.volumeUsd - a.volumeUsd);
  else if (sort === 'roi') out.sort((a, b) => b.roiPct - a.roiPct);
  else if (sort === 'eveScore') {
    const score = (r: LeaderboardRow): number => {
      if (r.accountValueUsd <= 0 || r.volumeUsd <= 0) return 0;
      return (
        Math.log10(r.accountValueUsd + 1) *
        Math.log10(r.volumeUsd + 1) *
        (r.roiPct / 100 + 1)
      );
    };
    out.sort((a, b) => score(b) - score(a));
  }
  // Rank-Spalte nach Sortierung neu vergeben (so dass sie immer 1..N läuft).
  return out.map((r, i) => ({ ...r, rank: i + 1 }));
}

function minAvCutoff(min: LeaderboardMinAv): number {
  if (min === 'all') return 0;
  if (min === '1k') return 1_000;
  if (min === '10k') return 10_000;
  if (min === '100k') return 100_000;
  return 1_000_000;
}

function avatarBg(tone: LeaderboardRow['avatarTone']): string {
  if (tone === 'emerald') return 'bg-emerald-500/20 text-emerald-300';
  if (tone === 'rose') return 'bg-rose-500/20 text-rose-300';
  if (tone === 'neutral') return 'bg-neutral-700 text-neutral-400';
  return 'bg-gradient-to-br from-amber-400 to-amber-600 text-neutral-950';
}

function fmtBig(value: number): string {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function shortAddr(addr: string): string {
  if (addr.length < 12) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-2)}`;
}
